/**
 * DriverNotificationsPage - Pagina de Notificacoes do Motorista
 *
 * Funcionalidades:
 * - Lista de notificacoes (rotas atribuidas, alertas, etc)
 * - Marcar como lido/nao lido
 * - Filtros por tipo
 * - Pull to refresh
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  IconButton,
  Chip,
  Skeleton,
  Button,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Notifications as NotificationIcon,
  NotificationsActive as ActiveIcon,
  Route as RouteIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  MoreVert as MoreIcon,
  DoneAll as ReadAllIcon,
  Refresh as RefreshIcon,
  Circle as UnreadIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';

// Tipos de notificacao
const NOTIFICATION_TYPES = {
  ROUTE_ASSIGNED: 'routeAssigned',
  ROUTE_STARTED: 'routeStarted',
  ROUTE_COMPLETED: 'routeCompleted',
  STOP_DELAYED: 'stopDelayed',
  ROUTE_DEVIATION: 'routeDeviation',
  GENERAL: 'general',
};

const getNotificationIcon = (type, tokens) => {
  switch (type) {
    case NOTIFICATION_TYPES.ROUTE_ASSIGNED:
      return <RouteIcon sx={{ color: tokens?.accent?.primary }} />;
    case NOTIFICATION_TYPES.ROUTE_COMPLETED:
      return <SuccessIcon sx={{ color: tokens?.semantic?.success }} />;
    case NOTIFICATION_TYPES.STOP_DELAYED:
    case NOTIFICATION_TYPES.ROUTE_DEVIATION:
      return <WarningIcon sx={{ color: tokens?.semantic?.warning }} />;
    default:
      return <InfoIcon sx={{ color: tokens?.text?.secondary }} />;
  }
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const NotificationCard = ({
  notification, tokens, onRead, onNavigate,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <Card
      sx={{
        mb: 1.5,
        backgroundColor: notification.read
          ? tokens?.surface?.raised
          : `${tokens?.accent?.primary}10`,
        borderRadius: tokens?.radius?.lg || '12px',
        border: `1px solid ${notification.read ? tokens?.border?.subtle : tokens?.accent?.primary}30`,
        position: 'relative',
      }}
    >
      <CardActionArea
        onClick={() => onNavigate?.(notification)}
        sx={{ p: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Icon */}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              backgroundColor: tokens?.surface?.elevated,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {getNotificationIcon(notification.type, tokens)}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {!notification.read && (
                <UnreadIcon sx={{ fontSize: 8, color: tokens?.accent?.primary }} />
              )}
              <Typography
                sx={{
                  color: tokens?.text?.primary,
                  fontWeight: notification.read ? 400 : 600,
                  fontSize: '14px',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {notification.title}
              </Typography>
              <Typography
                sx={{
                  color: tokens?.text?.tertiary,
                  fontSize: '11px',
                  flexShrink: 0,
                }}
              >
                {formatTime(notification.eventTime || notification.serverTime)}
              </Typography>
            </Box>
            <Typography
              sx={{
                color: tokens?.text?.secondary,
                fontSize: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {notification.message || notification.description}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>

      {/* Menu */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setMenuAnchor(e.currentTarget);
        }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: tokens?.text?.tertiary,
        }}
      >
        <MoreIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: { backgroundColor: tokens?.surface?.raised },
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onRead?.(notification, !notification.read);
          }}
        >
          {notification.read ? 'Marcar como nao lido' : 'Marcar como lido'}
        </MenuItem>
      </Menu>
    </Card>
  );
};

const NotificationSkeleton = ({ tokens }) => (
  <Card
    sx={{
      mb: 1.5,
      backgroundColor: tokens?.surface?.raised,
      borderRadius: tokens?.radius?.lg || '12px',
      border: `1px solid ${tokens?.border?.subtle}`,
      p: 2,
    }}
  >
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Skeleton variant="rounded" width={44} height={44} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="70%" height={20} />
        <Skeleton variant="text" width="90%" height={16} />
      </Box>
    </Box>
  </Card>
);

const DriverNotificationsPage = () => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, unread

  // Carregar notificacoes
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/driver/notifications');
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Filtrar notificacoes
  const filteredNotifications = notifications.filter((n) => {
    if (filter === 'unread') return !n.read;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Handlers
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/driver/notifications');
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Failed to refresh notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMarkRead = useCallback(async (notification, read) => {
    try {
      if (read) {
        await fetch(`/api/driver/notifications/${notification.id}/read`, { method: 'POST' });
      }
      setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, read } : n)));
    } catch (error) {
      console.error('Failed to mark notification:', error);
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await fetch('/api/driver/notifications/read-all', { method: 'POST' });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, []);

  const handleNavigate = useCallback((notification) => {
    // Marcar como lido ao clicar
    if (!notification.read) {
      handleMarkRead(notification, true);
    }

    // Navegar para rota se tiver routePlanId
    if (notification.routePlanId) {
      navigate(`/driver/routes/${notification.routePlanId}`);
    }
  }, [navigate, handleMarkRead]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ActiveIcon sx={{ color: tokens?.accent?.primary }} />
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 700,
              fontSize: '20px',
            }}
          >
            Notificacoes
          </Typography>
          {unreadCount > 0 && (
            <Chip
              size="small"
              label={unreadCount}
              sx={{
                height: 22,
                backgroundColor: tokens?.accent?.primary,
                color: 'white',
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={handleRefresh} disabled={loading}>
            <RefreshIcon sx={{ color: tokens?.text?.secondary }} />
          </IconButton>
          {unreadCount > 0 && (
            <IconButton size="small" onClick={handleMarkAllRead}>
              <ReadAllIcon sx={{ color: tokens?.text?.secondary }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          label="Todas"
          onClick={() => setFilter('all')}
          sx={{
            backgroundColor: filter === 'all' ? tokens?.accent?.primary : tokens?.surface?.elevated,
            color: filter === 'all' ? 'white' : tokens?.text?.secondary,
            '&:hover': { opacity: 0.9 },
          }}
        />
        <Chip
          label={`Nao lidas (${unreadCount})`}
          onClick={() => setFilter('unread')}
          sx={{
            backgroundColor: filter === 'unread' ? tokens?.accent?.primary : tokens?.surface?.elevated,
            color: filter === 'unread' ? 'white' : tokens?.text?.secondary,
            '&:hover': { opacity: 0.9 },
          }}
        />
      </Box>

      {/* Lista */}
      {loading ? (
        <>
          <NotificationSkeleton tokens={tokens} />
          <NotificationSkeleton tokens={tokens} />
          <NotificationSkeleton tokens={tokens} />
        </>
      ) : filteredNotifications.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            color: tokens?.text?.tertiary,
          }}
        >
          <NotificationIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
          <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
            {filter === 'unread' ? 'Nenhuma notificacao nao lida' : 'Nenhuma notificacao'}
          </Typography>
          <Typography sx={{ fontSize: '13px', mt: 0.5 }}>
            Voce sera notificado quando houver atualizacoes
          </Typography>
        </Box>
      ) : (
        filteredNotifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            tokens={tokens}
            onRead={handleMarkRead}
            onNavigate={handleNavigate}
          />
        ))
      )}
    </Box>
  );
};

export default DriverNotificationsPage;
